export interface SalaryAnalysisRangeInput {
  min: number;
  max: number;
  sampleSize?: number;
}

export type SalaryPlacement = 'below' | 'within' | 'above' | 'unknown';
export type SalarySpreadComparison = 'tighter' | 'similar' | 'wider' | 'unknown';

export interface SalaryContextAnalysis {
  placement: SalaryPlacement;
  spreadComparison: SalarySpreadComparison;
  differencePercent: number | null;
  jobMidpoint: number | null;
  marketMidpoint: number | null;
  sampleSize: number | null;
}

function isValidPositive(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function midpoint(range: SalaryAnalysisRangeInput | null | undefined): number | null {
  if (!range) return null;
  if (!isValidPositive(range.min) || !isValidPositive(range.max)) return null;
  return (range.min + range.max) / 2;
}

function spreadPercent(range: SalaryAnalysisRangeInput | null | undefined, mid: number | null): number | null {
  if (!range || !isValidPositive(mid)) return null;
  const width = range.max - range.min;
  if (!Number.isFinite(width) || width < 0) return null;
  return (width / mid) * 100;
}

export function buildSalaryContextAnalysis(
  jobRange: SalaryAnalysisRangeInput | null | undefined,
  marketRange: SalaryAnalysisRangeInput | null | undefined,
): SalaryContextAnalysis {
  const jobMidpoint = midpoint(jobRange);
  const marketMidpoint = midpoint(marketRange);
  const sampleSize =
    marketRange && typeof marketRange.sampleSize === 'number' && Number.isFinite(marketRange.sampleSize)
      ? marketRange.sampleSize
      : null;

  if (!isValidPositive(marketMidpoint)) {
    return {
      placement: 'unknown',
      spreadComparison: 'unknown',
      differencePercent: null,
      jobMidpoint,
      marketMidpoint: null,
      sampleSize,
    };
  }

  const differencePercent =
    isValidPositive(jobMidpoint) ? Math.round(((jobMidpoint - marketMidpoint) / marketMidpoint) * 100) : null;

  let placement: SalaryPlacement = 'unknown';
  if (isValidPositive(jobMidpoint) && marketRange) {
    if (jobMidpoint < marketRange.min) placement = 'below';
    else if (jobMidpoint > marketRange.max) placement = 'above';
    else placement = 'within';
  }

  const jobSpread = spreadPercent(jobRange, jobMidpoint);
  const marketSpread = spreadPercent(marketRange, marketMidpoint);
  let spreadComparison: SalarySpreadComparison = 'unknown';
  if (isValidPositive(jobSpread) && isValidPositive(marketSpread)) {
    const gap = jobSpread - marketSpread;
    if (Math.abs(gap) <= 8) spreadComparison = 'similar';
    else if (gap < 0) spreadComparison = 'tighter';
    else spreadComparison = 'wider';
  }

  return {
    placement,
    spreadComparison,
    differencePercent,
    jobMidpoint,
    marketMidpoint,
    sampleSize,
  };
}
